import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Row, Col, Container, Card } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import remarkHtml from "remark-html";
import insightsData from "../../StaticData/InsightsData";
import "./PostDetails.css";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import rehypeRaw from "rehype-raw";
import { motion } from "framer-motion";

const PostDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [post, setPost] = useState(null);
  const [postTitle, setPostTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");

  const handleNavigation = (e, url) => {
    e.preventDefault();
    // Block dangerous schemes and external URLs
    if (
      !url ||
      /^(javascript|data|vbscript|file):/i.test(url.trim()) ||
      /^\/\//.test(url.trim()) ||
      /^https?:\/\//i.test(url.trim())
    ) {
      console.warn("Blocked unsafe URL:", url);
      return;
    }
    // Re-encode query param names and values to prevent HPP injection
    const [pathPart, queryPart] = url.split("?");
    if (!queryPart) {
      navigate(pathPart);
      return;
    }
    const sanitizedQuery = queryPart
      .split("&")
      .map((param) => {
        const eqIndex = param.indexOf("=");
        if (eqIndex === -1) return encodeURIComponent(decodeURIComponent(param));
        const name = param.substring(0, eqIndex);
        const value = param.substring(eqIndex + 1);
        return `${encodeURIComponent(decodeURIComponent(name))}=${encodeURIComponent(decodeURIComponent(value))}`;
      })
      .join("&");
    navigate(`${pathPart}?${sanitizedQuery}`);
  };

  useEffect(() => {
    // FIX: decode the slug but DO NOT strip characters — slugs can contain dots,
    // tildes, and other valid URL chars. We only decode percent-encoding.
    // The find() below matches against your InsightsData slugs exactly.
    const safeSlug = slug ? decodeURIComponent(slug) : "";

    const currentPost = insightsData.find((p) => p.slug === safeSlug);
    if (currentPost) {
      setPost(currentPost);
      setPostTitle(currentPost.title);
      import(`../Insights/${currentPost.filename}`)
        .then((res) => fetch(res.default))
        .then((res) => res.text())
        .then((text) => {
          setContent(text);
          const sentences = text.split(". ");
          if (sentences.length > 0) {
            setMetaDescription(sentences[0] + ".");
          }
        });
    }
  }, [slug]);

  if (!post || !content) return null;

  const relatedPosts = post.tags
    ? insightsData
      .filter(
        (p) =>
          p.slug !== post.slug &&
          p.tags &&
          p.tags.some((tag) => post.tags.includes(tag))
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3)
    : [];

  return (
    <div>
      <Helmet>
        <title>{postTitle} - Idexcel | Blog</title>
        <meta name="description" content={metaDescription} />
        <script src="https://cyncsoftware.com/analytics.js"></script>
      </Helmet>

      <motion.div
        className="Productright"
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="post-banner">
          <Row className="h-100 align-items-center">
            <Col lg={8}>
              <div className="post-banner-content h-100 align-items-center">
                <motion.div
                  className="Productleft"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.7 }}
                >
                  <h1 style={{ fontSize: "62px" }}>{post.title}</h1>
                </motion.div>
              </div>
            </Col>
          </Row>
        </div>
      </motion.div>

      <div className="post-content">
        <Row>
          <Col lg={12}>
            <ReactMarkdown remarkPlugins={[remarkHtml]} rehypePlugins={[rehypeRaw]}>
              {content}
            </ReactMarkdown>
            <br />
            {post.keywords && (
              <div className="post-keywords">
                {post.keywords.map((keyword) => (
                  <Link
                    key={keyword}
                    to={`/keywords/${encodeURIComponent(keyword)}`}
                    onClick={(e) =>
                      handleNavigation(e, `/keywords/${encodeURIComponent(keyword)}`)
                    }
                  >
                    <span className="keyword">{keyword}</span>
                  </Link>
                ))}
              </div>
            )}
          </Col>
        </Row>
      </div>

      {relatedPosts.length > 0 && (
        <div className="related-posts">
          <Container>
            <h1>Related Posts</h1>
            <div className="space"></div>
            <Row xs={1} md={3} className="g-4">
              {relatedPosts.map((insight) => (
                <Col key={insight.id}>
                  <Card className="h-100">
                    <p className="date">{insight.date}</p>
                    <Card.Img
                      variant="top"
                      src={`${insight.imageName}`}
                      alt={`Insight ${insight.id}`}
                    />
                    <Card.Body className="insightsbody">
                      <div className="tags">
                        {insight.tags.map((tag, index) => (
                          <span key={tag}>
                            <Link
                              to={`/tag/${encodeURIComponent(tag)}`}
                              className="tag-link boxtags"
                              onClick={(e) =>
                                handleNavigation(e, `/tag/${encodeURIComponent(tag)}`)
                              }
                            >
                              {tag}
                            </Link>
                            {index < insight.tags.length - 1 && <span> | </span>}
                          </span>
                        ))}
                      </div>
                      <Link
                        to={insight.buttonLink}
                        onClick={(e) => handleNavigation(e, insight.buttonLink)}
                      >
                        <Card.Title>{insight.title}</Card.Title>
                      </Link>
                    </Card.Body>
                    <Link
                      to={insight.buttonLink}
                      onClick={(e) => handleNavigation(e, insight.buttonLink)}
                      className="read-more"
                    >
                      {insight.buttonLabel}
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        style={{ margin: "5px", float: "right" }}
                      />
                    </Link>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </div>
      )}
      <div className="space"></div>
    </div>
  );
};

export default PostDetail;
